<script>
  import { Combobox } from "bits-ui";
  import Fuse from "fuse.js";
  import { tick } from "svelte";
  import { IconSearch } from "@urbaninstitute/dataviz-components/icons";

  /**
   * @typedef {Object} Item
   * @property {string} value
   * @property {string} label
   * @property {boolean} [disabled]
   */

  /**
   * @typedef {{
   *   items?: Item[];
   *   placeholder?: string;
   *   value?: string;
   *   open?: boolean;
   *   maxWidth?: number;
   *   inputProps?: Record<string, any>;
   *   contentProps?: Record<string, any>;
   *   type?: "single" | "multiple";
   *   [key: string]: any;
   * }} Props
   */

  /** @type {Props} */
  let {
    items = [],
    placeholder = "Search...",
    value = $bindable(),
    open = $bindable(false),
    inputProps,
    contentProps,
    maxWidth = 0,
    type = "single",
    ...restProps
  } = $props();

  /** @type {string} */
  // Current text shown in the combobox input (search query or selected label).
  let searchValue = $state("");
  // True while the user is actively typing or starting a fresh search.
  let isEditing = $state(false);
  // Captures the selected value before a fresh search so we can restore it if nothing new is picked.
  let valueBeforeSearch = $state(/** @type {string | undefined} */ (undefined));
  let inputRef = $state(/** @type {HTMLInputElement | null} */ (null));
  let viewportRef = $state(/** @type {HTMLElement | null} */ (null));

  // Item matching the externally bound selected value.
  let activeItem = $derived(items.find((item) => item.value === value) || null);

  $effect(() => {
    if (isEditing) return;
    searchValue = activeItem?.label ?? "";
  });

  const fuzzy = $derived(new Fuse(items, { keys: ["label", "value"], isCaseSensitive: false }));

  /** @type {Item[]} */
  const filteredItems = $derived.by(() => {
    if (searchValue === "") return items;
    return fuzzy.search(searchValue).map((result) => result.item);
    // return items.filter((item) => item.label.toLowerCase().includes(searchValue.toLowerCase()));
  });

  let widthString = $derived(
    maxWidth > 0 ? `${maxWidth}px` : activeItem ? `${activeItem?.label?.length + 4}ch` : undefined
  );

  /**
   * @param {Event & { currentTarget: HTMLInputElement }} e
   */
  async function handleInput(e) {
    inputProps?.oninput?.(e);
    isEditing = true;
    searchValue = e.currentTarget.value;
    open = true;

    // Ensure keyboard highlight starts at the first filtered item while typing.
    await tick();
    if (viewportRef) {
      viewportRef.scrollTop = 0;
    }
    if (open && filteredItems.length > 0) {
      (inputRef ?? e.currentTarget)?.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Home", bubbles: true })
      );
    }
  }

  function startFreshSearch() {
    isEditing = true;
    valueBeforeSearch = value;
    searchValue = "";
    open = true;
  }

  /**
   * @param {FocusEvent & { currentTarget: HTMLInputElement }} e
   */
  function handleFocus(e) {
    inputProps?.onfocus?.(e);
    if (value) startFreshSearch();
    else open = true;
  }

  /**
   * @param {boolean} newOpen
   */
  function handleOpenChange(newOpen) {
    restProps?.onOpenChange?.(newOpen);
    open = newOpen;
    if (!newOpen) {
      isEditing = false;
      if (!value && valueBeforeSearch) {
        value = valueBeforeSearch;
      }
      valueBeforeSearch = undefined;
      searchValue = activeItem?.label ?? "";
    }
  }
</script>

<div
  class="flex items-center combobox-wrapper relative mt-3"
  class:no-item={!value}
  style={widthString ? `--input-width: ${widthString}` : ""}
>
  <Combobox.Root
    {type}
    {items}
    bind:value
    bind:open
    {...restProps}
    onOpenChange={handleOpenChange}
    inputValue={searchValue}
  >
    <div class="relative">
      <Combobox.Input
        bind:ref={inputRef}
        {placeholder}
        aria-label={placeholder}
        {...inputProps}
        oninput={handleInput}
        onfocus={handleFocus}
        class={`focus:ring focus:ring-gray-dark border border-solid border-[var(--color-gray)] bg-white py-2 md:text-l! text-l text-[var(--color-gray-darker)] pl-3 focus:ring-offset-4 focus:outline-hidden min-w-[200px] max-w-full font-sans w-[var(--input-width)]`}
      />
      <Combobox.Trigger
        class="absolute right-px top-px bottom-px w-8 flex items-center justify-center bg-white"
      >
        <span><IconSearch size={16} /></span>
      </Combobox.Trigger>
    </div>

    <Combobox.Portal disabled>
      <Combobox.Content
        {...contentProps}
        style={widthString ? `--input-width: ${widthString}` : ""}
        class="h-60 max-h-200 z-500 w-[var(--input-width)] min-w-[var(--input-width)] border border-solid border-[var(--color-gray)] bg-white outline-hidden"
      >
        <Combobox.Viewport bind:this={viewportRef}>
          {#each filteredItems as item (item.value)}
            <Combobox.Item
              value={item.value}
              label={item.label}
              disabled={item.disabled}
              class="data-highlighted:bg-gray-lighter data-highlighted:text-gray-darkest data-selected:bg-blue p-1 data-selected:text-white"
            >
              {#snippet children({ selected })}
                <span>{item.label}</span>
              {/snippet}
            </Combobox.Item>
          {:else}
            <div>No results found</div>
          {/each}
        </Combobox.Viewport>
      </Combobox.Content>
    </Combobox.Portal>
  </Combobox.Root>
</div>

<style>
  :global(.print-wrap .combobox-wrapper.no-item) {
    display: none !important;
  }
</style>
